/**
 * useBrandTheme — per-workspace brand theming (Push 5-post-smoke-43, 2026-07-15).
 *
 * Sibling to useTheme (light/dark). Whereas useTheme flips the whole app
 * palette between the light + dark schemes, useBrandTheme layers a
 * workspace-specific brand color + accent color + logo on top of whichever
 * palette is active.
 *
 * Data source: useSession(). The /api/me response carries
 *   brand_primary_color, brand_accent_color, brand_logo_r2_key,
 *   brand_logo_updated_at
 * from sys__account (Migration 0154). NULL on any field means "use Olytic
 * default" — the provider only OVERRIDES the corresponding CSS custom
 * property when the column carries a value.
 *
 * CSS var overrides (injected via a <style id="workspace-brand-theme"> tag
 * in document.head):
 *   --olytic-teal            → brand_primary_color
 *   --fg-brand               → brand_primary_color (semantic alias)
 *   --olytic-teal-soft       → rgba(brand, 0.28)
 *   --olytic-teal-tint       → rgba(brand, 0.08)
 *   --olytic-gold            → brand_accent_color
 *   --accent, --rule         → brand_accent_color (semantic aliases)
 *   --rule-soft              → rgba(brand_accent, 0.45)
 *   --fg-label               → brand_accent_color (semantic alias)
 *
 * Explicitly NOT overridden:
 *   --success, --danger, --warning, --olytic-green, --olytic-red,
 *   --olytic-amber (and derivatives) — semantic status colors must stay
 *   consistent across workspaces so error / success / warning UX reads
 *   the same for every user.
 *
 * Third-party brand marks (BrandLogo for OpenAI / Anthropic / Google /
 * connectors) inherit their own vendor hex from ConnectorLogo — they
 * ignore the CSS var overrides.
 *
 * Icon inheritance: Icon.tsx primitives paint with stroke="currentColor"
 * so anywhere an icon container sets color: var(--olytic-teal) the icon
 * automatically picks up the override. No per-icon code change needed.
 *
 * The provider mounts inside SessionProvider so it can read the current
 * user via useSession(). Auth-shell pages (sign-in / sign-up / verify-
 * email) render before session hydration; the provider is a no-op on
 * those (user === null → skip injection → Olytic defaults show).
 */

import { type ReactNode, useEffect } from 'react';
import { readBrandCache, writeBrandCache } from '../lib/brand-cache.ts';
import { useSession } from './useSession.tsx';

const STYLE_TAG_ID = 'workspace-brand-theme';

/**
 * Sub-agent KKKKK Track 1 (2026-07-29) — synchronous, module-init cache
 * hydration. Runs once at module load (before React mounts), reads the
 * localStorage brand cache, and injects the style tag + favicon
 * override so first paint matches the last-known workspace brand
 * instead of the Olytic default (previously the SPA rendered with
 * defaults until /api/me resolved, visible as a hard theme flip on
 * login / hard reload). See lib/brand-cache.ts for the storage
 * primitives; extracted there to avoid a circular import between
 * useBrandTheme + useSession (both need cache access — writer + reader
 * here, clearer on sign-out).
 *
 * Trade-off: on the first visit to a workspace the cache is empty and
 * the paint gap still happens once. Every subsequent visit paints
 * correctly on first frame.
 */
function applyBrandCache(): void {
  if (typeof document === 'undefined') return;
  const cache = readBrandCache();
  if (cache === null) return;
  injectBrandStyleTag(cache.brand_primary_color, cache.brand_accent_color);
  applyDynamicFavicon(cache.account_id, cache.brand_logo_r2_key, cache.brand_logo_updated_at);
}

function injectBrandStyleTag(primary: string | null, accent: string | null): void {
  if (typeof document === 'undefined') return;

  if (primary === null && accent === null) {
    const existing = document.getElementById(STYLE_TAG_ID);
    if (existing !== null) existing.remove();
    return;
  }

  const parts: string[] = [];

  if (primary !== null) {
    const primaryRgb = hexToRgb(primary);
    if (primaryRgb !== null) {
      parts.push(`--olytic-teal: ${primary};`);
      parts.push(`--fg-brand: ${primary};`);
      parts.push(
        `--olytic-teal-soft: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.28);`,
      );
      parts.push(
        `--olytic-teal-tint: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.08);`,
      );
    }
  }

  if (accent !== null) {
    const accentRgb = hexToRgb(accent);
    if (accentRgb !== null) {
      parts.push(`--olytic-gold: ${accent};`);
      parts.push(`--accent: ${accent};`);
      parts.push(`--rule: ${accent};`);
      parts.push(`--fg-label: ${accent};`);
      parts.push(`--rule-soft: rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.45);`);
      parts.push(`--olytic-gold-20: rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.20);`);
      parts.push(`--olytic-gold-40: rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.40);`);
    }
  }

  const css = `:root { ${parts.join(' ')} }`;

  let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (tag === null) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

// Module-init cache application — runs before React mounts so first paint
// carries the last-known workspace brand (no more Olytic-default flash).
applyBrandCache();

/**
 * Push 72 — dynamic favicon swap. When the workspace has uploaded a
 * custom brand logo, we point the browser tab icon at
 * /workspace-logo/{account_id} so the tab reads as the customer's brand,
 * not Olytic. Falls back to /icon-192.png on any error or missing logo
 * key. Same treatment for the alternate .ico link (removed rather than
 * overridden because the R2 proxy serves WebP/PNG, not multi-res .ico).
 */
function applyDynamicFavicon(
  accountId: string | null | undefined,
  logoR2Key: string | null | undefined,
  logoUpdatedAt: string | null | undefined,
): void {
  const primary = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/png"]');
  const primaryHrefDefault = '/icon-192.png';
  if (primary === null) return;

  if (
    accountId === null ||
    accountId === undefined ||
    logoR2Key === null ||
    logoR2Key === undefined ||
    logoR2Key === ''
  ) {
    // Reset to Olytic default.
    if (primary.href.endsWith(primaryHrefDefault) === false) {
      primary.href = primaryHrefDefault;
      primary.type = 'image/png';
    }
    return;
  }

  const v =
    logoUpdatedAt === null || logoUpdatedAt === undefined ? '1' : encodeURIComponent(logoUpdatedAt);
  // R2 proxy serves WebP/PNG most commonly; the png type hint is close
  // enough for the browser to pick up whatever the proxy returns.
  primary.type = 'image/png';
  primary.href = `/workspace-logo/${accountId}?v=${v}`;
}

export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    applyDynamicFavicon(
      user?.account_id ?? null,
      user?.brand_logo_r2_key ?? null,
      user?.brand_logo_updated_at ?? null,
    );
  }, [user?.account_id, user?.brand_logo_r2_key, user?.brand_logo_updated_at]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const primary = user?.brand_primary_color ?? null;
    const accent = user?.brand_accent_color ?? null;

    injectBrandStyleTag(primary, accent);

    // Sub-agent KKKKK Track 1 (2026-07-29) — refresh the cache so the
    // next mount paints correctly on first frame. Only cache when we
    // have a signed-in user (account_id present) — auth-shell pages
    // pre-hydration shouldn't overwrite a real workspace's cache.
    if (user?.account_id !== undefined && user?.account_id !== null) {
      writeBrandCache({
        account_id: user.account_id,
        brand_primary_color: primary,
        brand_accent_color: accent,
        brand_logo_r2_key: user.brand_logo_r2_key ?? null,
        brand_logo_updated_at: user.brand_logo_updated_at ?? null,
      });
    }
  }, [
    user?.account_id,
    user?.brand_primary_color,
    user?.brand_accent_color,
    user?.brand_logo_r2_key,
    user?.brand_logo_updated_at,
  ]);

  return <>{children}</>;
}

/**
 * Parse a `#RRGGBB` hex string into { r, g, b } (0-255 each). Returns null
 * when the input is malformed — caller falls through to "don't inject an
 * rgba variant" rather than corrupting the style block.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (m === null || m[1] === undefined) return null;
  const int = parseInt(m[1], 16);
  return {
    r: (int >> 16) & 0xff,
    g: (int >> 8) & 0xff,
    b: int & 0xff,
  };
}

/**
 * Build the /workspace-logo/{account_id} URL with a cache-buster tied to
 * brand_logo_updated_at. Returns null when the workspace has no logo set
 * (SPA falls back to the default Olytic mark).
 */
export function workspaceLogoUrl(
  accountId: string | null | undefined,
  logoR2Key: string | null | undefined,
  logoUpdatedAt: string | null | undefined,
): string | null {
  if (
    accountId === null ||
    accountId === undefined ||
    logoR2Key === null ||
    logoR2Key === undefined
  ) {
    return null;
  }
  const v =
    logoUpdatedAt === null || logoUpdatedAt === undefined ? '1' : encodeURIComponent(logoUpdatedAt);
  return `/workspace-logo/${accountId}?v=${v}`;
}
